import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2,
  Smartphone,
  CreditCard,
  Download,
  HelpCircle,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import {
  buildSquarePosUrl,
  detectPlatform,
  getSquareStoreUrl,
} from "@/lib/squarePos";

const SQUARE_TUTO_SEEN_KEY = "saor_square_pos_tuto_seen_v3";

/**
 * Driver-side payment card — iteration 74.
 *
 * On combine les 2 mondes :
 *   1. Deep-link Square POS avec **montant pré-rempli** (comportement
 *      attendu par l'utilisateur). La callback URL passée à Square est
 *      strictement `https://saor.online/square/pos-callback` (zero query
 *      params) pour matcher exactement ce qui est enregistré dans le
 *      Square Developer Dashboard.
 *   2. Le booking_id + token sont stockés dans `localStorage` juste avant
 *      la navigation → relus par la page /square/pos-callback au retour.
 *   3. Bouton "Paiement reçu" en backup si jamais Square ne revient pas
 *      auto vers SAOR (driver clos manuellement).
 *
 * Le webhook Square est idempotent : si le webhook arrive après que le
 * driver a déjà cliqué "Paiement reçu", il ne re-fait rien.
 */
export default function PaymentQRCard({ bookingId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tutoOpen, setTutoOpen] = useState(false);
  const [tutoHide, setTutoHide] = useState(false);
  const [confirming, setConfirming] = useState(false); // kept for confirmPaid (auto-fallback if needed)
  const [launching, setLaunching] = useState(false);
  const [readyUrl, setReadyUrl] = useState(null);
  const platform = detectPlatform();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get(`/driver/bookings/${bookingId}/payment-link`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.detail ||
              "Impossible de charger les informations de paiement",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Poll silencieux toutes les 5 s — quand le webhook Square confirme
    // le paiement en arrière-plan, la carte bascule auto sur "Payé".
    const t = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [bookingId]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        <p className="mt-2">Chargement…</p>
      </div>
    );
  }
  if (!data?.price) return null;

  if (data.paid) {
    return (
      <div
        className="rounded-3xl border-2 border-emerald-500/40 bg-emerald-50 p-6 text-center sm:p-7"
        data-testid="driver-payment-paid"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
        <p className="mt-2 text-base font-semibold text-emerald-700">
          Paiement confirmé · {data.price} €
        </p>
      </div>
    );
  }

  // Récupère le payload Square (montant cents + callback URL exact +
  // booking_id + token) et construit l'URL deep-link plateforme.
  const fetchSquareUrl = async () => {
    try {
      const res = await api.get(
        `/driver/bookings/${bookingId}/square-payload`,
      );
      const payload = res.data;
      // Stocke booking_id + token dans localStorage — la callback URL
      // passée à Square est "nue" pour matcher exactement le Dashboard.
      try {
        localStorage.setItem(
          "saor_square_pending_payment",
          JSON.stringify({
            booking_id: payload.booking_id,
            token: payload.token,
            ts: Date.now(),
          }),
        );
      } catch (_) {}
      return buildSquarePosUrl({
        amountCents: payload.amount_cents,
        callbackUrl: payload.callback_url,
        note: payload.note,
        state: payload.state,
      });
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Impossible de préparer le paiement Square.",
      );
      return null;
    }
  };

  const navigateToSquare = (url) => {
    if (!url) return;
    window.location.href = url;
    // Si l'app Square POS n'est pas installée, le scheme custom ne fait
    // rien sur iOS. On propose un install après 2 s si la page reste
    // visible (= l'app n'a pas pris le relais).
    setTimeout(() => {
      if (document.visibilityState === "visible") {
        toast.message("L'app Square POS ne s'est pas ouverte ?", {
          description:
            "Installez-la depuis l'App Store ou Google Play pour encaisser.",
          action: {
            label: "Installer",
            onClick: () => {
              window.location.href = getSquareStoreUrl();
            },
          },
        });
      }
    }, 2000);
  };

  const onPayClick = async () => {
    if (platform === "unknown") {
      toast.error(
        "Ouvrez SAOR depuis un téléphone iPhone ou Android pour encaisser via Square.",
      );
      return;
    }
    const seen = (() => {
      try {
        return localStorage.getItem(SQUARE_TUTO_SEEN_KEY) === "1";
      } catch (_) {
        return false;
      }
    })();
    setLaunching(true);
    const url = await fetchSquareUrl();
    setLaunching(false);
    if (!url) return;
    if (seen) {
      navigateToSquare(url);
    } else {
      setReadyUrl(url);
      setTutoOpen(true);
    }
  };

  const handleTutoConfirm = () => {
    if (tutoHide) {
      try {
        localStorage.setItem(SQUARE_TUTO_SEEN_KEY, "1");
      } catch (_) {}
    }
    // ⚠️ Navigation synchrone AVANT le setTutoOpen — sinon le re-render
    // React casse le user gesture iOS et le scheme custom est bloqué.
    navigateToSquare(readyUrl);
    setTutoOpen(false);
  };

  const confirmPaid = async () => {
    setConfirming(true);
    try {
      await api.post(`/driver/bookings/${bookingId}/confirm-square-paid`);
      toast.success("Paiement confirmé — vous pouvez clôturer la course.");
      const res = await api.get(`/driver/bookings/${bookingId}/payment-link`);
      setData(res.data);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Impossible de confirmer le paiement.",
      );
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div
      className="rounded-3xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-card to-card p-6 shadow-sm sm:p-7"
      data-testid="driver-payment-square"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary">
        <Smartphone className="h-3.5 w-3.5" />
        Encaissement Tap-to-Pay · Square
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Montant à encaisser :{" "}
        <strong className="text-foreground text-base">{data.price} €</strong>
        <br />
        <span className="text-[11px]">
          Le montant sera pré-rempli automatiquement dans Square POS.
        </span>
      </p>

      {(() => {
        // Surface the processing fee (cents kept by SAOR) so the driver
        // sees the exact breakdown of what's NOT included in their share.
        const price = Number(data.price || 0);
        const fee = +(price - Math.floor(price)).toFixed(2);
        if (fee <= 0) return null;
        return (
          <div className="mt-3 rounded-lg border border-border bg-secondary/40 p-3 text-[11px] text-muted-foreground" data-testid="processing-fee-driver">
            <p>
              Sur ce montant, <strong className="text-foreground">{fee.toFixed(2)} €</strong> correspondent à des <strong>frais de traitement</strong> (non comptés dans votre part). Votre commission est calculée sur <strong className="text-foreground">{Math.floor(price)} €</strong>.
            </p>
          </div>
        );
      })()}

      <div className="mt-5 flex flex-col items-stretch gap-3">
        <Button
          size="lg"
          onClick={onPayClick}
          disabled={launching || platform === "unknown"}
          className="h-14 rounded-full gap-2 text-base font-semibold"
          data-testid="open-square-pos-button"
        >
          {launching ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Smartphone className="h-5 w-5" />
          )}
          Tap to Pay · Encaisser {data.price} €
        </Button>

        <div className="flex justify-center gap-4 text-[11px] text-muted-foreground">
          <button
            type="button"
            onClick={() => setTutoOpen(true)}
            className="inline-flex items-center gap-1 underline-offset-2 hover:underline hover:text-primary"
            data-testid="reopen-square-tuto"
          >
            <HelpCircle className="h-3 w-3" />
            Comment ça marche ?
          </button>
          <a
            href={getSquareStoreUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline-offset-2 hover:underline hover:text-primary"
            data-testid="install-square-app-link"
          >
            <Download className="h-3 w-3" />
            Installer l'app Square
          </a>
        </div>
      </div>

      {platform === "unknown" && (
        <p className="mt-3 rounded-2xl border border-amber-400/50 bg-amber-50 p-3 text-[11px] text-amber-800">
          ⚠️ Ouvrez SAOR sur votre iPhone ou Android pour encaisser via Square.
        </p>
      )}

      {/* Tutorial modal */}
      <Dialog open={tutoOpen} onOpenChange={setTutoOpen}>
        <DialogContent className="max-w-md" data-testid="square-tuto-modal">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] text-xl">
              Encaisser via Square — 3 étapes
            </DialogTitle>
            <DialogDescription>
              Le montant{" "}
              <strong className="text-foreground">{data.price} €</strong>
              {" "}sera pré-rempli automatiquement dans Square POS.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <Step
              icon={<ExternalLink className="h-5 w-5" />}
              n="1"
              text={
                <>
                  Touchez <strong>« Encaisser via Square »</strong>. L'app
                  Square POS s'ouvre avec le montant déjà rempli.
                </>
              }
            />
            <Step
              icon={<CreditCard className="h-5 w-5" />}
              n="2"
              text={
                <>
                  Présentez la carte du client (ou son téléphone avec
                  Apple/Google Pay) au dos de votre téléphone pour le paiement
                  sans contact.
                </>
              }
            />
            <Step
              icon={<CheckCircle2 className="h-5 w-5" />}
              n="3"
              text={
                <>
                  Square renvoie automatiquement vers SAOR et la course passe
                  en <strong>« Payée »</strong>. Vous pouvez alors la clôturer.
                </>
              }
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={tutoHide}
              onChange={(e) => setTutoHide(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-primary"
              data-testid="square-tuto-hide"
            />
            Ne plus afficher ce guide
          </label>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setTutoOpen(false)}
              className="rounded-full"
              data-testid="square-tuto-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={handleTutoConfirm}
              className="rounded-full gap-1.5"
              data-testid="square-tuto-confirm"
            >
              <ExternalLink className="h-4 w-4" />
              Compris, ouvrir Square ({data.price} €)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Step({ icon, n, text }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border bg-secondary/40 p-3">
      <div className="grid h-9 w-9 flex-none place-items-center rounded-full bg-primary/15 text-primary">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">
          Étape {n}
        </p>
        <p className="text-sm text-foreground">{text}</p>
      </div>
    </div>
  );
}
